import * as React from 'react';
import { Search, X, Plus, Loader2, CheckCircle2 } from 'lucide-react';

import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { GlassModal } from '@/components/ui/glass-modal';
import { SearchInput } from '@/components/ui/search-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { JobProgress } from '@/components/ui/JobProgress';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { useMyItems } from '@/hooks/useUserItems';
import { useMarkAsSold } from '@/hooks/useTransactions';
import { useMarkSoldStore } from '@/store/markSoldStore';
import { formatUsd } from '@/lib/formatMoney';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import type { UserItem } from '@/types/userItem';

interface BasketRow {
	item: UserItem;
	price: string; // dollars, editable
}

const thumbFor = (item: UserItem) =>
	item.images?.[0]?.image_url || item.stock_image_url || null;

const subtitleFor = (item: UserItem) =>
	[item.set_name, item.card_number ? `#${item.card_number}` : null]
		.filter(Boolean)
		.join(' · ');

function MarkSoldBody({ onClose }: { onClose: () => void }) {
	const { data: items = [], isLoading } = useMyItems();
	const markAsSold = useMarkAsSold();

	const [query, setQuery] = React.useState('');
	const [basket, setBasket] = React.useState<BasketRow[]>([]);
	const [saleDate, setSaleDate] = React.useState(() => new Date().toISOString().slice(0, 10));
	const [submitting, setSubmitting] = React.useState(false);
	const [progress, setProgress] = React.useState({ done: 0, total: 0, failed: 0 });

	const basketIds = React.useMemo(() => new Set(basket.map((b) => b.item.id)), [basket]);

	const available = React.useMemo(
		() => items.filter((i) => !i.sold_at && !basketIds.has(i.id)),
		[items, basketIds]
	);

	const results = React.useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return available.slice(0, 8);
		const tokens = q.split(/\s+/);
		return available
			.filter((i) => {
				const hay = `${i.title ?? ''} ${i.set_name ?? ''} ${i.card_number ?? ''} ${i.set_code ?? ''}`.toLowerCase();
				return tokens.every((t) => hay.includes(t));
			})
			.slice(0, 20);
	}, [available, query]);

	const addItem = (item: UserItem) => {
		const cents = item.asking_price_cents ?? 0;
		setBasket((prev) => [...prev, { item, price: cents > 0 ? (cents / 100).toFixed(2) : '' }]);
		setQuery('');
	};

	const removeItem = (id: string) => setBasket((prev) => prev.filter((b) => b.item.id !== id));

	const setPrice = (id: string, price: string) =>
		setBasket((prev) => prev.map((b) => (b.item.id === id ? { ...b, price } : b)));

	const totalCents = basket.reduce((sum, b) => {
		const n = parseFloat(b.price);
		return sum + (isFinite(n) && n > 0 ? Math.round(n * 100) : 0);
	}, 0);

	const canSubmit = !submitting && basket.length > 0;

	const handleSubmit = async () => {
		if (!canSubmit) return;
		setSubmitting(true);
		const saleDateObj = new Date(saleDate + 'T12:00:00');
		let success = 0;
		let failed = 0;
		setProgress({ done: 0, total: basket.length, failed: 0 });

		for (const row of basket) {
			const n = parseFloat(row.price);
			const cents = isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
			try {
				await markAsSold.mutateAsync({
					itemId: row.item.id,
					salePriceCents: cents,
					paymentMethod: 'cash',
					saleDate: saleDateObj,
				});
				success++;
			} catch (e) {
				console.error('Mark sold failed for', row.item.id, e);
				failed++;
			}
			setProgress({ done: success + failed, total: basket.length, failed });
		}

		setSubmitting(false);

		if (success > 0) {
			if (failed > 0) toast.error(`${success} marked sold · ${failed} failed`);
			setBasket([]);
			onClose();
		} else {
			toast.error('Failed to mark items sold');
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div>
				<h2 className="text-base font-semibold text-foreground">Mark cards as sold</h2>
				<p className="text-xs text-muted-foreground mt-0.5">
					Search your inventory, stack up everything you sold, then finalize once.
				</p>
			</div>

			{/* Search */}
			<div className="relative">
				<Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
				<SearchInput
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search inventory…"
					className="pl-10 pr-9 h-11 bg-black/20 border-white/10"
					disabled={submitting}
				/>
				{query && (
					<button
						type="button"
						onClick={() => setQuery('')}
						aria-label="Clear search"
						className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
					>
						<X className="w-4 h-4" />
					</button>
				)}
			</div>

			{/* Results */}
			<div className="max-h-[34dvh] overflow-y-auto overscroll-contain rounded-xl border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.05]">
				{isLoading ? (
					<div className="flex items-center justify-center py-8 text-muted-foreground">
						<Loader2 className="w-4 h-4 animate-spin" />
					</div>
				) : results.length === 0 ? (
					<div className="py-8 text-center text-sm text-muted-foreground">
						{query ? 'No matching cards' : 'Nothing left to mark sold'}
					</div>
				) : (
					results.map((item) => {
						const thumb = thumbFor(item);
						return (
							<button
								key={item.id}
								type="button"
								onClick={() => addItem(item)}
								disabled={submitting}
								className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/[0.04] active:bg-white/[0.07] transition-colors"
							>
								<div className="h-11 w-8 shrink-0 rounded-md overflow-hidden bg-white/[0.05]">
									{thumb && <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />}
								</div>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium truncate text-foreground">{item.title}</div>
									<div className="text-[11px] text-muted-foreground truncate">{subtitleFor(item) || '—'}</div>
								</div>
								<span className="text-sm tabular-nums text-active-gold shrink-0">
									{item.asking_price_cents ? formatUsd(item.asking_price_cents / 100) : '—'}
								</span>
								<Plus className="w-4 h-4 shrink-0 text-muted-foreground" />
							</button>
						);
					})
				)}
			</div>

			{/* Basket */}
			{basket.length > 0 && (
				<div className="space-y-2">
					<div className="flex items-center justify-between px-0.5">
						<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Selling {basket.length}
						</span>
						<span className="text-sm font-semibold tabular-nums text-active-gold">
							{formatUsd(totalCents / 100)}
						</span>
					</div>
					<div className="max-h-[28dvh] overflow-y-auto overscroll-contain space-y-2 pr-0.5">
						{basket.map((row) => (
							<div
								key={row.item.id}
								className="flex items-center gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.03] px-2.5 py-2"
							>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium truncate text-foreground">{row.item.title}</div>
									<div className="text-[11px] text-muted-foreground truncate">{subtitleFor(row.item) || '—'}</div>
								</div>
								<div className="relative w-24 shrink-0">
									<span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
									<Input
										{...PRICE_INPUT_PROPS}
										value={row.price}
										onChange={(e) => setPrice(row.item.id, e.target.value.replace(/[^0-9.]/g, ''))}
										placeholder="0.00"
										disabled={submitting}
										aria-label={`Sale price for ${row.item.title}`}
										className="h-9 pl-6 bg-black/20 border-white/10 text-sm"
									/>
								</div>
								<button
									type="button"
									onClick={() => removeItem(row.item.id)}
									disabled={submitting}
									aria-label={`Remove ${row.item.title}`}
									className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.06]"
								>
									<X className="w-4 h-4" />
								</button>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Sale date */}
			<div className="space-y-1.5">
				<Label htmlFor="quick-sold-date" className="text-sm">Sale date</Label>
				<Input
					id="quick-sold-date"
					type="date"
					value={saleDate}
					onChange={(e) => setSaleDate(e.target.value)}
					disabled={submitting}
					className="h-10 bg-black/20 border-white/10"
				/>
			</div>

			{submitting && (
				<JobProgress label="Marking sold" done={progress.done} total={progress.total} failed={progress.failed} isRunning />
			)}

			<p className="text-[11px] text-muted-foreground/70">
				Sold items move to Sold and are pulled from eBay automatically. Blank price uses the item's listed price.
			</p>

			<Button
				onClick={handleSubmit}
				disabled={!canSubmit}
				className={cn(
					'h-11 w-full font-semibold border-0',
					'bg-active-gold/95 hover:bg-active-gold text-black'
				)}
			>
				{submitting ? (
					<>
						<Loader2 className="w-4 h-4 mr-2 animate-spin" />
						Marking {progress.done} of {progress.total}…
					</>
				) : (
					<>
						<CheckCircle2 className="w-4 h-4 mr-2" />
						{basket.length > 0 ? `Mark ${basket.length} sold` : 'Add cards to continue'}
					</>
				)}
			</Button>
		</div>
	);
}

/** Global quick "Mark Cards as Sold" tray — bottom sheet on mobile, glass modal on desktop. */
export function QuickMarkSoldDialog() {
	const open = useMarkSoldStore((s) => s.open);
	const setOpen = useMarkSoldStore((s) => s.setOpen);
	const isMobile = useIsMobile();
	const { keyboardHeight } = useVisualViewportInset();

	const close = React.useCallback(() => setOpen(false), [setOpen]);

	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={setOpen}>
				<SheetContent
					side="bottom"
					className="max-h-[92dvh] overflow-y-auto bg-chrome border-white/[0.08] rounded-t-2xl px-4"
					style={{ paddingBottom: keyboardHeight ? 24 : 'calc(env(safe-area-inset-bottom) + 20px)' }}
				>
					<SheetTitle className="sr-only">Mark cards as sold</SheetTitle>
					<SheetDescription className="sr-only">
						Search your inventory and mark multiple cards sold at once.
					</SheetDescription>
					{open && <MarkSoldBody onClose={close} />}
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<GlassModal
			open={open}
			onOpenChange={setOpen}
			title="Mark cards as sold"
			description="Search your inventory and mark multiple cards sold at once."
			desktopMaxWidth={520}
		>
			{open && <MarkSoldBody onClose={close} />}
		</GlassModal>
	);
}
